import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HardHat, Pencil, Trash2 } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RoleGate } from "@/components/auth/RoleGate";
import { techniciansService } from "@/services/technicians.service";
import { getErrorMessage } from "@/services/api";
import { formatCPF, formatPhone, initialsOf, mediaUrl } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";

export default function TechniciansListPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ sortBy: "created_at", sortOrder: "desc" });
    const [confirm, setConfirm] = useState(null);

    const query = useQuery({
        queryKey: ["technicians", { page, search, ...sort }],
        queryFn: () =>
            techniciansService.list({
                page,
                pageSize: 20,
                search,
                sortBy: sort.sortBy,
                sortOrder: sort.sortOrder,
            }),
    });

    const del = useMutation({
        mutationFn: (id) => techniciansService.remove(id),
        onSuccess: () => {
            toast.success("Técnico removido.");
            qc.invalidateQueries({ queryKey: ["technicians"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Não foi possível remover técnico.")),
    });

    const columns = [
        {
            key: "name",
            header: "Técnico",
            sortable: true,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                        {r.photo_url ? <AvatarImage src={mediaUrl(r.photo_url)} alt={r.name} /> : null}
                        <AvatarFallback className="bg-muted text-xs">
                            {initialsOf(r.name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{r.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                            {r.position || "—"}
                        </div>
                    </div>
                </div>
            ),
        },
        { key: "cpf", header: "CPF", cell: (r) => formatCPF(r.cpf) || "—" },
        {
            key: "engineering_specialty",
            header: "Especialidade",
            cell: (r) =>
                r.engineering_specialty ? (
                    <Badge variant="outline" className="text-[10px]">{r.engineering_specialty}</Badge>
                ) : (
                    "—"
                ),
        },
        {
            key: "professional_registration",
            header: "Registro",
            cell: (r) => r.professional_registration || "—",
        },
        { key: "phone", header: "Telefone", cell: (r) => formatPhone(r.phone) || "—" },
        {
            key: "status",
            header: "Status",
            cell: (r) =>
                r.status === "active" ? (
                    <Badge variant="outline" className="border-success/40 text-success">Ativo</Badge>
                ) : (
                    <Badge variant="outline" className="text-muted-foreground">Inativo</Badge>
                ),
        },
        {
            key: "actions",
            header: "",
            className: "w-24",
            cell: (r) => (
                <div className="flex justify-end gap-1">
                    <RoleGate resource="technicians" action="update">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/technicians/${r.id}`);
                            }}
                            data-testid={`technicians-edit-${r.id}`}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                    <RoleGate resource="technicians" action="delete">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-danger hover:text-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                setConfirm(r);
                            }}
                            data-testid={`technicians-delete-${r.id}`}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Técnicos"
                description="Cadastro de técnicos e engenheiros da operação."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "Técnicos" }]}
            />
            <DataTable
                dataTestId="technicians"
                columns={columns}
                query={query.data}
                loading={query.isLoading}
                onSearch={(t) => {
                    setPage(1);
                    setSearch(t);
                }}
                onPageChange={setPage}
                onSortChange={setSort}
                onCreate={can("technicians", "create") ? () => navigate("/technicians/new") : null}
                createLabel="Novo técnico"
                onRowClick={(r) => can("technicians", "update") && navigate(`/technicians/${r.id}`)}
                emptyState={{
                    title: "Sem técnicos",
                    description: "Cadastre o primeiro técnico da operação.",
                    cta: "Novo técnico",
                }}
                initialSort={sort}
            />
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Excluir técnico?"
                description={`\"${confirm?.name || ""}\" será removido permanentemente.`}
                confirmLabel="Excluir"
                variant="destructive"
                onConfirm={() => {
                    if (confirm) del.mutate(confirm.id);
                    setConfirm(null);
                }}
            />
        </div>
    );
}
